import { jwtDecode } from "jwt-decode";

import {
    LS_BROWSER_SESSION_ID_KEY,
    LS_LANG_KEY,
    LS_SIGNATURE_KEY,
    LS_TOKEN_KEY,
    LS_USER_KEY,
} from "constants/constants";
import { getUrl } from "services/urlService";
import { Scenario } from "types/scenarioType";
import {
    HTTPResponseError,
    NotFoundError,
    UnknownError,
} from "services/errors";
import { SessionConnectionInfo, SessionType } from "types/sessionType";
import { User } from "types/User";
import { Customization, Subdomain } from "services/customizationService";

const { fetch: originalFetch } = window;
const fetch = async (...args: any): Promise<any> => {
    const JWT_EXPIRED = "JWT_EXPIRED";
    const JWT_USER_UNKNOWN = "JWT_USER_UNKNOWN";

    const [resource, config] = args;

    const response = await originalFetch(resource, config);
    if (!response.ok && 401 === response.status) {
        try {
            const message = await response.text();
            if ([JWT_EXPIRED, JWT_USER_UNKNOWN].includes(message)) {
                localStorage.removeItem(LS_TOKEN_KEY);
                localStorage.removeItem(LS_BROWSER_SESSION_ID_KEY);
                localStorage.removeItem(LS_USER_KEY);

                //Here we try to identify what code online the user is trying to access to
                //By using regex on url path
                const codeOnlineRegexp = /[A-Z]{6}/;
                const codeOnline =
                    window.location.href.match(codeOnlineRegexp)?.[0] ?? null;

                if (codeOnline) {
                    window.location.href = `/login/${codeOnline}`;
                } else {
                    window.location.href = "/";
                }

                //Prevent next code to be executed till new location is loaded
                await new Promise((r) => setTimeout(r, 15 * 1000));
            } else {
                return response;
            }
        } catch {
            return response;
        }
    }
    return response;
};

export enum ADDITIONAL_FIELDS_API {
    PROXIES = "proxies",
    IS_SIGNED = "isSigned",
    IS_PHOTO_RECEIVED = "isPhotoReceived",
    LIVE_DEVICE_COUNT = "liveDeviceCount",
}

type RootPath = "api" | "logout" | "login" | "form";

type PathApi = `/${RootPath}${string}`;

/**
 * Will explain the getURL because we need to get the subdomain sometime
 */
const fetchApi = (path: PathApi, params?: RequestInit): Promise<Response> => {
    const Authorization = localStorage.getItem(LS_TOKEN_KEY)
        ? `Bearer ${localStorage.getItem(LS_TOKEN_KEY)}`
        : "";

    const { headers, body, ...paramsWithoutHeaders } = params ?? {};

    const isFormData = body instanceof FormData;

    const signature = localStorage.getItem(LS_SIGNATURE_KEY);

    return fetch(`${getUrl()}${path}`, {
        headers: {
            ...(isFormData ? {} : { "Content-Type": "application/json" }),
            Authorization,
            "X-Online-Spa": "spa",
            "session-id": localStorage.getItem(LS_BROWSER_SESSION_ID_KEY) ?? "",
            ...(signature ? { "session-signature": signature } : {}),
            "Accept-Language": localStorage.getItem(LS_LANG_KEY) ?? "",
            ...headers,
        },
        body,
        ...paramsWithoutHeaders,
    });
};

export const sendPhoto = async (blob: Blob): Promise<User | null> => {
    const formData = new FormData();
    formData.append("photo", blob, "photo.webp");

    const res = await fetchApi(`/api/user/me/photo`, {
        method: "PUT",
        body: formData,
    });

    if (!res.ok) throw new HTTPResponseError(res);

    return await res.json();
};

export const checkCodeExists = (sessionCode: string): Promise<Response> =>
    fetchApi(`/api/check-code/${sessionCode}`);

export const getSessionConnectionInfo = async (
    sessionCode: string,
): Promise<SessionConnectionInfo> => {
    const res: Response = await fetchApi(
        `/api/session/${sessionCode}/connection-information`,
    );

    if (!res.ok) {
        if (res.status === 404) {
            throw new NotFoundError();
        } else {
            throw new UnknownError();
        }
    }

    return await res.json();
};

export const getSession = async (
    sessionCode: string,
    args?: Array<string>,
): Promise<SessionType> => {
    const res: Response = await fetchApi(
        `/api/session/${sessionCode}/front${
            args && args.length > 0 ? "?additionalFields=" + args.join(",") : ""
        }`,
    );

    if (!res.ok) {
        throw new HTTPResponseError(res);
    }

    return await res.json();
};

export const getCurrentUser = async (
    args?: Array<ADDITIONAL_FIELDS_API>,
    token?: string | null,
): Promise<User> => {
    let headers = {};
    if (token) {
        const { sessionID } = jwtDecode(token) as {
            sessionID: string;
        };

        headers = {
            "session-id": sessionID,
            Authorization: `Bearer ${token}`,
        };
    }

    const res: Response = await fetchApi(
        `/api/user/me${
            args && args.length > 0 ? "?additionalFields=" + args.join(",") : ""
        }`,
        {
            headers,
        },
    );

    if (!res.ok) {
        throw new HTTPResponseError(res);
    }

    return await res.json();
};

export const loginFormScenario = async (
    sessionCode: string,
    scenario: Scenario,
    data: any,
): Promise<string | null> => {
    const res: Response = await fetchApi(`/form/scenario`, {
        method: "POST",
        redirect: "manual",
        body: JSON.stringify({ codeOnline: sessionCode, scenario, ...data }),
    });

    if (!res.ok) {
        throw new HTTPResponseError(res);
    }

    const response = await res.json();

    return response.token;
};

export const loginEmailPassword = async ({
    email,
    password,
    codeOnline,
}: {
    email: string;
    password: string;
    codeOnline: string;
}): Promise<string> => {
    const res: Response = await fetchApi(`/form/scenario/email_password`, {
        method: "POST",
        redirect: "manual",
        body: JSON.stringify({
            email,
            password,
            codeOnline,
        }),
    });

    if (!res.ok) {
        throw new HTTPResponseError(res);
    }

    const response = await res.json();

    if (!response.token) {
        throw new Error();
    }
    return response.token;
};

export const loginEmailPasswordSms = async ({
    email,
    password,
    codeOnline,
    verificationCode,
}: {
    email: string;
    password: string;
    codeOnline: string;
    verificationCode: string;
}): Promise<string> => {
    const res: Response = await fetchApi(`/form/scenario/email_password_sms`, {
        method: "POST",
        redirect: "manual",
        body: JSON.stringify({
            email,
            password,
            codeOnline,
            verificationCode,
        }),
    });

    if (!res.ok) {
        throw new HTTPResponseError(res);
    }

    const response = await res.json();

    if (!response.token) {
        throw new Error();
    }

    return response.token;
};

export const requestSmsVerificationCode = async ({
    email,
    password,
    codeOnline,
}: {
    email: string;
    password: string;
    codeOnline: string;
}): Promise<string | null> => {
    const res: Response = await fetchApi(
        `/api/request-verification-code/${codeOnline}`,
        {
            method: "POST",
            redirect: "manual",
            body: JSON.stringify({
                email,
                password,
            }),
        },
    );

    if (!res.ok) {
        throw new HTTPResponseError(res);
    }

    const response = await res.json();

    return response.phoneNumber;
};

export const requestEmailPassword = async ({
    email,
    codeOnline,
}: {
    email: string;
    codeOnline: string;
}): Promise<void> => {
    const res: Response = await fetchApi(
        `/api/request-password/${codeOnline}`,
        {
            method: "POST",
            redirect: "manual",
            body: JSON.stringify({
                email,
            }),
        },
    );

    if (!res.ok) {
        throw res.status;
    }
};

export const requestEmailPin = async ({
    email,
    codeOnline,
}: {
    email: string;
    codeOnline: string;
}): Promise<void> => {
    const res: Response = await fetchApi(`/api/request-pin/${codeOnline}`, {
        method: "POST",
        redirect: "manual",
        body: JSON.stringify({
            email,
        }),
    });
    if (!res.ok) {
        throw res.status;
    }
};

export const logOut = async (): Promise<void> => {
    const res: Response = await fetchApi(`/logout`, {
        method: "POST",
    });

    if (!res.ok) {
        throw new HTTPResponseError(res);
    }
};

export const sendTagCloudToDisplay = async (
    sessionCode: string,
    response: {
        words: string[];
        id: string | undefined;
    },
): Promise<void> => {
    const res: Response = await fetchApi(
        `/api/session/${sessionCode}/tag-cloud`,
        {
            method: "POST",
            body: JSON.stringify(response),
        },
    );
    if (!res.ok) {
        throw new HTTPResponseError(res);
    }
};

export const performSignature = async (blob: Blob): Promise<User | null> => {
    const formData = new FormData();
    formData.append("signature", blob, "signature.png");

    const res = await fetchApi(`/api/user/me/sign`, {
        method: "PUT",
        body: formData,
    });

    if (!res.ok) throw new HTTPResponseError(res);

    return await res.json();
};

//Allow to find session from associated subdomain
export const reverseSessionSubdomain = async (): Promise<string | null> => {
    const res: Response = await fetchApi(`/api/session/reverse-subdomain`, {
        method: "GET",
    });
    if (!res.ok) {
        return null;
    }

    const response = await res.json();
    return response.codeOnline;
};

export const getSubdomainFromSession = async (
    sessionCode: string,
): Promise<Subdomain | null> => {
    const res: Response = await fetchApi(
        `/api/session/${sessionCode}/subdomain`,
        {
            method: "GET",
        },
    );

    if (!res.ok) {
        return null;
    }

    return await res.json();
};

export const getCustomization = async (): Promise<Customization | null> => {
    const res: Response = await fetchApi(`/api/session/customization`, {
        method: "GET",
    });
    if (!res.ok) {
        return null;
    }

    return await res.json();
};

export const getIsPseudoAvailable = async (
    sessionCode: string,
    pseudo: string,
): Promise<boolean | null> => {
    const res: Response = await fetchApi(
        `/api/session/${sessionCode}/check-pseudo-availability/${pseudo}`,
    );
    if (!res.ok) {
        return null;
    }

    const response = await res.json();
    return Boolean(response.isPseudoAvailable);
};
